import {
  Stepper as MantineStepper,
  StepperProps as MantineStepperProps,
} from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { SVGIcon } from '../icons/SVGIcon';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';

import styles from './stepper.module.scss';

const MAX_STEPS = 8;

export type StepperStep = {
  label: string;
  content?: React.ReactNode;
};

export type StepperProps = {
  steps: StepperStep[];
  active?: number;
  defaultActive?: number;
  onStepClick?: (step: number) => void;
  classNames?: MantineStepperProps['classNames'];
  allowStepClick?: boolean;
} & Omit<
  MantineStepperProps,
  'steps' | 'active' | 'onStepClick' | 'classNames' | 'size' | 'children'
>;

const DEFAULT_ACTIVE = 0;

export function Stepper({
  steps,
  active: activeProp,
  defaultActive,
  onStepClick: onStepClickProp,
  classNames,
  allowStepClick = true,
  ...props
}: StepperProps) {
  const [active, setActive] = useUncontrolled({
    value: activeProp,
    defaultValue: defaultActive,
    onChange: onStepClickProp,
    finalValue: DEFAULT_ACTIVE,
  });

  return (
    <MantineStepper
      {...props}
      size="xs"
      active={active}
      onStepClick={setActive}
      classNames={{
        root: styles.root,
        steps: styles.steps,
        separator: styles.separator,
        step: styles.step,
        stepIcon: styles.stepIcon,
        stepLabel: styles.stepLabel,
        verticalSeparator: styles.verticalSeparator,
        ...classNames,
      }}
      completedIcon={<SVGIcon name="check" />}
    >
      {steps.slice(0, MAX_STEPS).map((step, index) => (
        <MantineStepper.Step
          key={`${step.label}-${index}`}
          label={
            <TextWithTooltip
              tooltipProps={{ position: 'right', usePortal: false }}
              textProps={{ lineClamp: 1 }}
            >
              {step.label}
            </TextWithTooltip>
          }
          allowStepSelect={allowStepClick}
        >
          {step.content}
        </MantineStepper.Step>
      ))}
    </MantineStepper>
  );
}
